import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { notifications as notificationsApi, workspaceApi, teamApi, planName } from "@/lib/api";
import type { NotificationSettings, WorkspaceMember, WorkspaceInvitation } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { PageHeader } from "@/components/layout/PageHeader";
import { Icon } from "@/components/ui/icon";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { BillingTab } from "@/components/billing/BillingTab";
import { DomainVerification } from "@/components/DomainVerification";
import { ApiKeysTab } from "@/components/settings/ApiKeysTab";
import { IntegrationsTab } from "@/components/settings/IntegrationsTab";
import { PluginsTab } from "@/components/settings/PluginsTab";
import {
  BrandLogo, SETTINGS_BUTTONS, SettingsBlock, SettingsPane, SettingsRow, SettingsSection, StatusPill,
} from "@/components/settings/SettingsLayout";

// Tab ids are part of URLs (?tab=…) linked from elsewhere, so keep them stable
const NAV: { group: string; items: { id: string; label: string; icon: string }[] }[] = [
  {
    group: "Alerts",
    items: [
      { id: "notifications", label: "Notifications", icon: "notification-01" },
      { id: "integrations", label: "Integrations", icon: "link-01" },
      { id: "plugins", label: "Plugins", icon: "plug-01" },
    ],
  },
  {
    group: "Workspace",
    items: [
      { id: "workspace", label: "General", icon: "settings-01" },
      { id: "team", label: "Team", icon: "user-group" },
      { id: "verification", label: "Domain", icon: "shield-check" },
    ],
  },
  {
    group: "Account",
    items: [
      { id: "billing", label: "Billing", icon: "credit-card" },
      { id: "api", label: "API", icon: "api" },
    ],
  },
];

const ALL_TABS = NAV.flatMap((g) => g.items);
export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab") ?? "notifications";
  const activeTab = ALL_TABS.some((t) => t.id === requested) ? requested : "notifications";

  const setTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  };

  // Show toast on return from checkout
  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout === "success") {
      toast.success("Subscription activated — welcome aboard!");
    } else if (checkout === "cancelled") {
      toast.info("Checkout cancelled. You can upgrade any time.");
    }
  }, []);

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Settings" />
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6 md:flex-row md:gap-10 md:py-8">
          <SettingsNav active={activeTab} onSelect={setTab} />
          <main className={cn("min-w-0 flex-1 pb-12", SETTINGS_BUTTONS)}>
            {activeTab === "notifications" && <NotificationsTab setTab={setTab} />}
            {activeTab === "integrations" && <IntegrationsTab />}
            {activeTab === "plugins" && <PluginsTab />}
            {activeTab === "workspace" && <WorkspaceTab setTab={setTab} />}
            {activeTab === "team" && <TeamTab />}
            {activeTab === "verification" && <VerificationTab setTab={setTab} />}
            {activeTab === "billing" && (
              <SettingsPane title="Billing" description="Your plan, invoices and payment method">
                <BillingTab />
              </SettingsPane>
            )}
            {activeTab === "api" && (
              <SettingsPane title="API" description="Keys for reading alerts and competitors from your own code">
                <ApiKeysTab />
              </SettingsPane>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// ─── Navigation ───────────────────────────────────────────────────────────────

function SettingsNav({ active, onSelect }: { active: string; onSelect: (id: string) => void }) {
  return (
    <nav aria-label="Settings" className={cn("md:w-48 md:shrink-0", SETTINGS_BUTTONS)}>
      {/* Mobile: one scrolling strip */}
      <div className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 pb-3 md:hidden">
        {ALL_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect(t.id)}
            className={cn(
              "shrink-0 rounded-md px-3 py-1.5 font-sans text-xs transition-colors",
              active === t.id ? "bg-foreground/[0.07] font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Desktop: grouped rail */}
      <div className="sticky top-8 hidden space-y-6 md:block">
        {NAV.map((g) => (
          <div key={g.group} className="space-y-1">
            <p className="px-2.5 pb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">{g.group}</p>
            {g.items.map((t) => {
              const on = active === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onSelect(t.id)}
                  aria-current={on ? "page" : undefined}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left font-sans text-xs transition-colors",
                    on ? "bg-foreground/[0.07] font-medium text-foreground" : "text-muted-foreground hover:bg-foreground/[0.03] hover:text-foreground",
                  )}
                >
                  <Icon name={t.icon} size={14} className={on ? "text-foreground" : "text-muted-foreground"} />
                  {t.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </nav>
  );
}

function FieldSkeletonSection({ rows }: { rows: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="space-y-1.5 border-b border-border px-5 py-4">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3 w-56" />
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center justify-between border-t border-border px-5 py-4 first:border-t-0">
          <div className="space-y-1.5">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-3 w-44" />
          </div>
          <Skeleton className="h-5 w-9 rounded-full" />
        </div>
      ))}
    </div>
  );
}

function SavingIndicator({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
      <Icon name="refresh" size={11} className="animate-spin" />Saving
    </span>
  );
}

// ─── Notifications Tab ────────────────────────────────────────────────────────

function NotificationsTab({ setTab }: { setTab: (tab: string) => void }) {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [slackUrl, setSlackUrl] = useState("");
  const [emailList, setEmailList] = useState("");

  useEffect(() => {
    notificationsApi.get().then((res) => {
      if ("data" in res && res.data) {
        setSettings(res.data);
        setSlackUrl(res.data.slackWebhookUrl ?? "");
        setEmailList(res.data.emailAddresses.join(", "));
      }
      setIsLoading(false);
    });
  }, []);

  const save = async (patch: Partial<NotificationSettings>) => {
    setIsSaving(true);
    const res = await notificationsApi.update(patch);
    if ("data" in res && res.data) {
      setSettings(res.data);
      toast.success("Saved");
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setIsSaving(false);
  };

  const handleTestSlack = async () => {
    const res = await notificationsApi.testSlack();
    if ("data" in res) toast.success("Test message sent");
    else toast.error("Failed");
  };

  const handleTestEmail = async () => {
    const res = await notificationsApi.testEmail();
    if ("data" in res) toast.success("Test email sent");
    else toast.error("Failed");
  };

  const savedEmails = settings?.emailAddresses.join(", ") ?? "";

  return (
    <SettingsPane
      title="Notifications"
      description="Choose where alerts land and how often"
      actions={<SavingIndicator show={isSaving} />}
    >
      {isLoading ? (
        <>
          <FieldSkeletonSection rows={1} />
          <FieldSkeletonSection rows={2} />
          <FieldSkeletonSection rows={2} />
        </>
      ) : (
        <>
          {/* Slack: the app lives under Integrations; a pasted webhook still works */}
          <SettingsSection
            icon={<BrandLogo domain="slack.com" name="Slack" fallbackIcon="slack" />}
            title="Slack"
            description="Post alerts to a Slack channel"
            actions={
              <Button size="sm" variant="outline" onClick={() => setTab("integrations")}>
                Manage in Integrations
                <Icon name="arrow-right-01" size={12} className="ml-1.5" />
              </Button>
            }
          >
            {(settings?.slackWebhookUrl || settings?.slackEnabled) && (
              <>
                <SettingsRow
                  label="Pasted webhook (legacy)"
                  hint="Still works, but the Slack app lets you pick a channel without any setup."
                >
                  <Switch checked={settings?.slackEnabled ?? false} onCheckedChange={(v) => save({ slackEnabled: v })} />
                </SettingsRow>
                <SettingsRow label="Webhook URL" stack>
                  <Input type="url" placeholder="https://hooks.slack.com/services/..."
                    value={slackUrl} onChange={(e) => setSlackUrl(e.target.value)} className="flex-1" />
                  <Button variant="outline" onClick={() => save({ slackWebhookUrl: slackUrl || null })}
                    disabled={slackUrl === (settings?.slackWebhookUrl ?? "")}>
                    Save
                  </Button>
                  {settings?.slackWebhookUrl && (
                    <Button variant="ghost" size="icon-sm" onClick={handleTestSlack} title="Send test" aria-label="Send test">
                      <Icon name="sent" size={13} />
                    </Button>
                  )}
                </SettingsRow>
              </>
            )}
          </SettingsSection>

          {/* Email */}
          <SettingsSection
            icon="mail-01"
            title="Email"
            description="Receive alerts and digests by email"
            actions={settings?.emailAddresses && settings.emailAddresses.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleTestEmail}>
                <Icon name="sent" size={12} className="mr-1.5" />Send test
              </Button>
            )}
          >
            <SettingsRow label="Email alerts" hint="Send each alert as it happens.">
              <Switch checked={settings?.emailEnabled ?? false} onCheckedChange={(v) => save({ emailEnabled: v })} />
            </SettingsRow>
            <SettingsRow label="Recipients" hint="Separate multiple addresses with commas." stack>
              <Input placeholder="you@company.com, teammate@company.com"
                value={emailList} onChange={(e) => setEmailList(e.target.value)} className="flex-1" />
              <Button
                variant="outline"
                onClick={() => save({ emailAddresses: emailList.split(",").map((e) => e.trim()).filter(Boolean) })}
                disabled={emailList.trim() === savedEmails}
              >
                Save
              </Button>
            </SettingsRow>
          </SettingsSection>

          {/* Digest */}
          <SettingsSection
            icon="calendar-01"
            title="Daily digest"
            description="Roll up low and medium signal changes into one daily message"
          >
            <SettingsRow label="Daily digest" hint="High-signal alerts still arrive straight away.">
              <Switch checked={settings?.digestEnabled ?? false} onCheckedChange={(v) => save({ digestEnabled: v })} />
            </SettingsRow>
            <SettingsRow label="Delivery time" hint="In UTC.">
              <Input type="time" value={settings?.digestTime ?? "09:00"}
                onChange={(e) => save({ digestTime: e.target.value })}
                disabled={!settings?.digestEnabled}
                className="w-32" />
            </SettingsRow>
          </SettingsSection>
        </>
      )}
    </SettingsPane>
  );
}

// ─── Team Tab ─────────────────────────────────────────────────────────────────

function Avatar({ children, dashed }: { children: ReactNode; dashed?: boolean }) {
  return (
    <div className={cn(
      "flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted font-mono text-[11px] uppercase",
      dashed ? "border-dashed border-border bg-muted/40" : "border-border",
    )}>
      {children}
    </div>
  );
}

function TeamTab() {
  const { user, workspace } = useAuth();
  const [inviteEmail, setInviteEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const isOwner = workspace?.ownerId === user?.id;

  const load = async () => {
    setIsLoading(true);
    const [membersRes, invitesRes] = await Promise.all([
      teamApi.members(),
      teamApi.invitations(),
    ]);
    if ("data" in membersRes && membersRes.data) setMembers(membersRes.data);
    if ("data" in invitesRes && invitesRes.data) setInvitations(invitesRes.data);
    setIsLoading(false);
  };

  useEffect(() => { load(); }, []);

  const inviteLink = `${(import.meta as { env?: { VITE_FRONTEND_URL?: string } }).env?.VITE_FRONTEND_URL ?? window.location.origin}/invite/${workspace?.slug ?? ""}`;

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Invite link copied");
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setIsSending(true);
    const res = await teamApi.invite(email);
    if ("data" in res && res.data) {
      toast.success(`Invite sent to ${email}`);
      setInviteEmail("");
      setInvitations((prev) => [res.data!, ...prev]);
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setIsSending(false);
  };

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    setRemovingId(memberId);
    const res = await teamApi.removeMember(memberId);
    if ("data" in res) {
      toast.success(`${memberEmail} removed from workspace`);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setRemovingId(null);
  };

  const handleRevokeInvitation = async (invitationId: string, email: string) => {
    setRevokingId(invitationId);
    const res = await teamApi.revokeInvitation(invitationId);
    if ("data" in res) {
      toast.success(`Invite to ${email} revoked`);
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    } else if ("error" in res) {
      toast.error(res.error.message);
    }
    setRevokingId(null);
  };

  const pendingInvites = invitations.filter((i) => i.status === "pending");

  const seatNote =
    workspace?.plan === "growth" ? "Each teammate adds $29/month to your Growth plan."
    : workspace?.plan === "starter" ? "Starter includes up to 3 seats."
    : undefined;

  const memberColumns: DataTableColumn<WorkspaceMember>[] = [
    {
      id: "member",
      header: "Member",
      cell: (m) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar>
            {m.avatarUrl ? <img src={m.avatarUrl} alt="" className="h-full w-full object-cover" /> : m.name[0]}
          </Avatar>
          <div className="min-w-0">
            <p className="truncate font-sans text-xs font-medium">
              {m.name}
              {m.email === user?.email && <span className="ml-1.5 font-normal text-muted-foreground">(you)</span>}
            </p>
            <p className="truncate font-sans text-xs text-muted-foreground">{m.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: "role",
      header: "Role",
      width: "7rem",
      cell: (m) => (
        <span className={cn("font-mono text-[11px] capitalize", m.role === "owner" ? "text-foreground" : "text-muted-foreground")}>
          {m.role}
        </span>
      ),
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      width: "3.5rem",
      align: "right",
      cell: (m) =>
        isOwner && m.email !== user?.email ? (
          <Button
            variant="ghost" size="icon-sm"
            className="text-muted-foreground hover:text-destructive md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100"
            disabled={removingId === m.id}
            onClick={() => handleRemoveMember(m.id, m.email)}
            title={`Remove ${m.name}`} aria-label={`Remove ${m.name}`}
          >
            <Icon name={removingId === m.id ? "refresh" : "user-remove-01"} size={13} className={removingId === m.id ? "animate-spin" : undefined} />
          </Button>
        ) : null,
    },
  ];

  const inviteColumns: DataTableColumn<WorkspaceInvitation>[] = [
    {
      id: "email",
      header: "Invited",
      cell: (inv) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar dashed><Icon name="mail-01" size={12} className="text-muted-foreground" /></Avatar>
          <p className="truncate font-sans text-xs font-medium">{inv.email}</p>
        </div>
      ),
    },
    {
      id: "expires",
      header: "Expires",
      width: "9rem",
      cell: (inv) => (
        <span className="font-mono text-[11px] text-muted-foreground">{new Date(inv.expiresAt).toLocaleDateString()}</span>
      ),
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      width: "3.5rem",
      align: "right",
      cell: (inv) =>
        isOwner ? (
          <Button
            variant="ghost" size="icon-sm"
            className="text-muted-foreground hover:text-destructive md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100"
            disabled={revokingId === inv.id}
            onClick={() => handleRevokeInvitation(inv.id, inv.email)}
            title="Revoke invite" aria-label={`Revoke invite to ${inv.email}`}
          >
            <Icon name={revokingId === inv.id ? "refresh" : "cancel-01"} size={13} className={revokingId === inv.id ? "animate-spin" : undefined} />
          </Button>
        ) : null,
    },
  ];

  return (
    <SettingsPane title="Team" description={`People with access to ${workspace?.name ?? "this workspace"}`}>
      <SettingsSection title="Invite members" description={seatNote}>
        <SettingsRow
          label="Invite by email"
          hint={!isOwner ? "Only workspace owners can invite members." : "They'll get an email with a link to join."}
          stack
        >
          <Input
            type="email"
            placeholder="colleague@company.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleInvite()}
            disabled={!isOwner}
            className="flex-1"
          />
          <Button onClick={handleInvite} disabled={isSending || !inviteEmail.trim() || !isOwner}>
            {isSending ? <Icon name="refresh" size={12} className="animate-spin" /> : "Send invite"}
          </Button>
        </SettingsRow>
        <SettingsRow label="Invite link" hint="Anyone with this link can ask to join." stack>
          <Input value={inviteLink} readOnly className="flex-1 font-mono text-[11px] text-muted-foreground" />
          <Button variant="outline" onClick={copyLink}>
            <Icon name={copied ? "checkmark-circle-01" : "copy-01"} size={13} className="mr-1.5" />
            {copied ? "Copied" : "Copy"}
          </Button>
        </SettingsRow>
      </SettingsSection>

      <div className="space-y-2">
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Members {!isLoading && <span className="tabular-nums">· {members.length}</span>}
        </p>
        {isLoading ? (
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 border-t border-border px-4 py-3 first:border-t-0">
                <Skeleton className="h-7 w-7 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-3 w-12" />
              </div>
            ))}
          </div>
        ) : (
          <DataTable columns={memberColumns} rows={members} rowKey={(m) => m.id} hideHeader />
        )}
      </div>

      {!isLoading && pendingInvites.length > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Pending invites <span className="tabular-nums">· {pendingInvites.length}</span>
          </p>
          <DataTable columns={inviteColumns} rows={pendingInvites} rowKey={(i) => i.id} />
        </div>
      )}
    </SettingsPane>
  );
}

// ─── Workspace Tab ────────────────────────────────────────────────────────────

function WorkspaceTab({ setTab }: { setTab: (tab: string) => void }) {
  const { workspace, refreshWorkspace, createWorkspace, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState(workspace?.name ?? "");
  const [domain, setDomain] = useState(workspace?.domain ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(searchParams.get("action") === "create");
  const [newWsName, setNewWsName] = useState("");
  const [newWsDomain, setNewWsDomain] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const createLock = useRef(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sync state when workspace loads
  useEffect(() => {
    if (workspace) {
      setName(workspace.name);
      setDomain(workspace.domain ?? "");
    }
  }, [workspace?.id]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    // Always clean domain before saving — strip protocol and www
    const cleanedDomain = domain
      ? domain.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].trim()
      : "";
    const res = await workspaceApi.update({
      name: name !== workspace?.name ? name : undefined,
      domain: cleanedDomain !== (workspace?.domain ?? "") ? (cleanedDomain || null) : undefined,
    });
    if ("data" in res) {
      toast.success("Workspace updated");
      if (cleanedDomain !== domain) setDomain(cleanedDomain);
      await refreshWorkspace(); // refresh sidebar logo immediately
    } else {
      toast.error("Failed to save");
    }
    setIsSaving(false);
  };

  const handleCreateWorkspace = async () => {
    if (!newWsName.trim() || createLock.current) return;
    createLock.current = true;
    setIsCreating(true);
    try {
      const result = await createWorkspace(newWsName.trim(), newWsDomain.trim() || null);
      if (result.error) {
        if (result.created) {
          setCreateOpen(false);
          setNewWsName("");
          setNewWsDomain("");
        }
        toast.error(result.error);
        return;
      }

      setCreateOpen(false);
      setNewWsName("");
      setNewWsDomain("");
      toast.success("Workspace created. Opening it now.");
      window.setTimeout(() => window.location.assign("/dashboard"), 400);
    } finally {
      createLock.current = false;
      setIsCreating(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!workspace) return;
    setIsDeleting(true);
    const res = await workspaceApi.delete();
    if ("data" in res && res.data) {
      setDeleteOpen(false);
      if (res.data.loggedOut) {
        toast.success("Workspace deleted");
        logout();
        return;
      }

      if (res.data.workspace) {
        toast.success("Workspace deleted. Switched to your remaining workspace.");
        window.location.href = "/dashboard";
        return;
      }
    } else if ("error" in res) {
      toast.error(res.error?.message ?? "Failed to delete workspace");
    }
    setIsDeleting(false);
  };

  const isDirty = name.trim() !== (workspace?.name ?? "") || domain !== (workspace?.domain ?? "");

  return (
    <SettingsPane title="General" description="Your workspace's name, domain and plan">
      <SettingsSection>
        <SettingsRow label="Workspace name" hint="Shown in the sidebar and in emails." stack>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()} className="flex-1" />
        </SettingsRow>
        <SettingsRow
          label="Company domain"
          hint={<>Just the domain, e.g. <span className="font-mono">yourcompany.com</span>. Mira uses it to understand your product.</>}
          stack
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
            {domain ? (
              <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} alt=""
                className="h-4 w-4 object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
            ) : (
              <Icon name="globe" size={14} className="text-muted-foreground" />
            )}
          </div>
          <Input
            value={domain}
            onChange={(e) => {
              // Auto-strip https://, http://, www. so users can't enter malformed domains
              setDomain(e.target.value.replace(/^https?:\/\//, "").replace(/^www\./, ""));
            }}
            placeholder="yourcompany.com"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            className="flex-1"
          />
          {workspace?.domainVerified && domain === workspace.domain ? (
            <StatusPill tone="ok">Verified</StatusPill>
          ) : domain && domain === workspace?.domain ? (
            <Button variant="outline" size="sm" onClick={() => setTab("verification")}>Verify</Button>
          ) : null}
        </SettingsRow>
        <SettingsBlock className="flex justify-end bg-muted/20 py-3">
          <Button size="sm" onClick={handleSave} disabled={isSaving || !isDirty || !name.trim()}>
            {isSaving && <Icon name="refresh" size={12} className="mr-1.5 animate-spin" />}
            Save changes
          </Button>
        </SettingsBlock>
      </SettingsSection>

      <SettingsSection>
        <SettingsRow label="Plan" hint="Starter $49/mo · Growth $149/mo + $29 per teammate">
          <span className="font-mono text-xs font-semibold">{planName(workspace?.plan)}</span>
          <Button size="sm" variant="outline" onClick={() => setTab("billing")}>
            {workspace?.plan === "growth" ? "Manage" : "Upgrade"}
          </Button>
        </SettingsRow>
        <SettingsRow label="Workspace ID" hint="Useful when contacting support or using the API.">
          <code className="max-w-[14rem] truncate font-mono text-[11px] text-muted-foreground">{workspace?.id}</code>
          <Button
            variant="ghost" size="icon-sm" aria-label="Copy workspace ID" title="Copy"
            onClick={() => { if (workspace?.id) { void navigator.clipboard.writeText(workspace.id); toast.success("Workspace ID copied"); } }}
          >
            <Icon name="copy-01" size={13} />
          </Button>
        </SettingsRow>
        <SettingsRow label="New workspace" hint="A separate workspace for a different company or project.">
          <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
            <Icon name="add-01" size={12} className="mr-1.5" />Create
          </Button>
        </SettingsRow>
      </SettingsSection>

      <SettingsSection tone="danger" title="Danger zone">
        <SettingsRow label="Delete workspace" hint="Permanently delete this workspace and all of its data.">
          <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
            Delete
          </Button>
        </SettingsRow>
      </SettingsSection>

      {/* Create workspace dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className={cn("max-w-sm", SETTINGS_BUTTONS)}>
          <DialogHeader>
            <DialogTitle>Create new workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Workspace name</Label>
              <Input placeholder="Acme Inc." value={newWsName}
                onChange={(e) => setNewWsName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateWorkspace()} />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Product domain <span className="normal-case tracking-normal text-muted-foreground/60">(optional)</span></Label>
              <Input
                placeholder="yourproduct.com"
                value={newWsDomain}
                onChange={(e) => setNewWsDomain(e.target.value.replace(/^https?:\/\//, "").replace(/^www\./, ""))}
                onKeyDown={(e) => e.key === "Enter" && handleCreateWorkspace()}
              />
              <p className="font-sans text-xs text-muted-foreground">Used so Mira knows your product when doing competitive analysis.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateWorkspace} disabled={isCreating || !newWsName.trim()}>
              {isCreating ? <Icon name="refresh" size={12} className="animate-spin mr-2" /> : null}
              {isCreating ? "Creating workspace..." : "Create workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete workspace confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className={cn("max-w-sm", SETTINGS_BUTTONS)}>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete workspace?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <p className="font-sans text-sm text-foreground">
              This will permanently delete <span className="font-semibold">{workspace?.name}</span> and all associated data, including:
            </p>
            <ul className="list-inside space-y-1 font-sans text-xs text-muted-foreground">
              <li>• All competitors and watched URLs</li>
              <li>• All alerts and notifications</li>
              <li>• Team members and invitations</li>
              <li>• All settings and configurations</li>
            </ul>
            <p className="font-sans text-xs text-destructive font-medium">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteWorkspace}
              disabled={isDeleting}
            >
              {isDeleting ? <Icon name="refresh" size={12} className="animate-spin mr-2" /> : null}
              Delete workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsPane>
  );
}

// ─── Verification Tab ─────────────────────────────────────────────────────────

const VERIFY_REASONS = [
  { icon: "shield-01", title: "Security", body: "Stops anyone else claiming your domain" },
  { icon: "checkmark-circle-01", title: "Trust", body: "Shows you legitimately own the domain" },
  { icon: "flash", title: "Features", body: "Unlocks advanced competitor analysis" },
  { icon: "user-group", title: "Branding", body: "Enables custom workspace branding and logos" },
];

function VerificationTab({ setTab }: { setTab: (tab: string) => void }) {
  const { workspace, refreshWorkspace } = useAuth();
  const [failedFavicon, setFailedFavicon] = useState(false);

  if (!workspace) {
    return (
      <SettingsPane title="Domain">
        <SettingsSection
          icon="alert-01"
          title="Couldn't load your workspace"
          description="There was an issue loading your workspace data. Please try refreshing the page."
        />
      </SettingsPane>
    );
  }

  return (
    <SettingsPane title="Domain" description="Prove you own your company's domain">
      <SettingsSection
        icon={
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40">
            {workspace.domain && !failedFavicon ? (
              <img src={`https://www.google.com/s2/favicons?domain=${workspace.domain}&sz=32`} alt=""
                className="h-4 w-4" onError={() => setFailedFavicon(true)} />
            ) : (
              <Icon name="globe" size={16} className="text-muted-foreground" />
            )}
          </div>
        }
        title={workspace.domain ?? "No domain set"}
        description={
          !workspace.domain ? "Add your company domain in General first."
          : workspace.domainVerified && workspace.domainVerifiedAt ? `Verified on ${new Date(workspace.domainVerifiedAt).toLocaleDateString()}`
          : workspace.domainVerified ? "Ownership confirmed"
          : "Follow the steps below to confirm you own it"
        }
        badge={workspace.domain && (
          workspace.domainVerified
            ? <StatusPill tone="ok">Verified</StatusPill>
            : <StatusPill tone="warn">Pending</StatusPill>
        )}
        actions={!workspace.domain && (
          <Button variant="outline" size="sm" onClick={() => setTab("workspace")}>Set domain</Button>
        )}
      >
        {workspace.domain && (
          <SettingsBlock>
            <DomainVerification domain={workspace.domain} onVerificationComplete={() => refreshWorkspace()} />
          </SettingsBlock>
        )}
      </SettingsSection>

      <SettingsSection title="Why verify?">
        <SettingsBlock className="grid gap-4 sm:grid-cols-2">
          {VERIFY_REASONS.map((r) => (
            <div key={r.title} className="flex gap-3">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40">
                <Icon name={r.icon} size={13} className="text-muted-foreground" />
              </div>
              <div className="space-y-0.5">
                <p className="font-sans text-xs font-medium">{r.title}</p>
                <p className="font-sans text-xs text-muted-foreground">{r.body}</p>
              </div>
            </div>
          ))}
        </SettingsBlock>
      </SettingsSection>
    </SettingsPane>
  );
}
